import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  Country,
  GridDrawerComponent,
  ResidencyJurisdiction,
} from '../../models';

@Component({
  standalone: false,
  selector: 'gstar-upsert-residency-jurisdiction',
  templateUrl: './upsert-residency-jurisdiction.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpsertResidencyJurisdictionComponent
  extends GridDrawerComponent<ResidencyJurisdiction>
  implements OnInit
{
  private readonly fb = inject(FormBuilder);

  public form: FormGroup = this.fb.group({
    country: ['', Validators.required],
    region: ['', Validators.required],
    isActive: true,
  });
  enableUpdate: boolean = false;

  destroyRef = inject(DestroyRef);

  get countryValue(): Country {
    return this.form.get('country')?.value;
  }

  get countryField(): AbstractControl<Country> {
    return this.form.get('country')!;
  }

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values: ResidencyJurisdiction) => {
        if (this.isEdit) this.checkChanges(values);
        this.modelChange.emit({
          model: {
            ...this.model,
            ...values,
          },
          prevModel: this.model!,
          isValid:
            this.isEdit && this.form.valid
              ? this.enableUpdate
              : this.form.valid,
        });
      });

    // Load form values once country list has been loaded
    this.loadFormValues();
  }

  loadFormValues(): void {
    if (!this.model) return;
    const currentValue = this.model as ResidencyJurisdiction;
    const selectedRegion = currentValue?.region ?? null;

    this.form.setValue({
      country: currentValue.country ?? null,
      region: selectedRegion ?? null,
      isActive: currentValue?.isActive,
    });
  }

  countrySelect(country: Country): void {
    this.form.patchValue({ country, region: country.region });
  }

  countryClear(): void {
    this.form.patchValue({ country: null, region: null });
  }

  private checkChanges(values: ResidencyJurisdiction): void {
    if (values && this.model) {
      this.enableUpdate = values.isActive !== this.model.isActive;
    }
  }
}
